import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Typography, Chip, TextField, InputAdornment, CircularProgress, Card, CardContent,
  Stack, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Switch, FormControlLabel, Alert, Fade, Zoom
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon,
  Refresh as RefreshIcon, AdminPanelSettings as RoleIcon, Security as SecurityIcon,
  Lock as LockIcon, LockOpen as LockOpenIcon, Close as CloseIcon, Save as SaveIcon,
  Shield as ShieldIcon, VerifiedUser as VerifiedUserIcon
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import axiosInstance from '../../services/axiosInstance';
import { useConfirm } from '../../hooks/useConfirm';

const translations = {
  en: {
    title: 'Roles & Permissions', subtitle: 'Manage user roles and access control',
    addRole: 'Add Role', roleName: 'Role Name', roleNameAr: 'Role Name (Arabic)',
    description: 'Description', isSystem: 'System Role', status: 'Status', actions: 'Actions',
    search: 'Search roles...', loading: 'Loading...', noData: 'No roles found', confirmDelete: 'Delete this role?',
    active: 'Active', inactive: 'Inactive', save: 'Save', cancel: 'Cancel', managePermissions: 'Manage Permissions',
    totalRoles: 'Total Roles', systemRoles: 'System Roles', customRoles: 'Custom Roles', activeRoles: 'Active',
    refresh: 'Refresh', edit: 'Edit', delete: 'Delete', createRole: 'Create New Role', editRole: 'Edit Role',
    system: 'System', custom: 'Custom', cannotModify: 'Cannot modify system role'
  },
  ar: {
    title: 'الأدوار والصلاحيات', subtitle: 'إدارة أدوار المستخدمين والتحكم بالوصول',
    addRole: 'إضافة دور', roleName: 'اسم الدور', roleNameAr: 'اسم الدور بالعربي',
    description: 'الوصف', isSystem: 'دور نظام', status: 'الحالة', actions: 'الإجراءات',
    search: 'البحث عن دور...', loading: 'جاري التحميل...', noData: 'لا توجد أدوار', confirmDelete: 'حذف هذا الدور؟',
    active: 'نشط', inactive: 'غير نشط', save: 'حفظ', cancel: 'إلغاء', managePermissions: 'إدارة الصلاحيات',
    totalRoles: 'إجمالي الأدوار', systemRoles: 'أدوار النظام', customRoles: 'أدوار مخصصة', activeRoles: 'نشط',
    refresh: 'تحديث', edit: 'تعديل', delete: 'حذف', createRole: 'إنشاء دور جديد', editRole: 'تعديل الدور',
    system: 'نظام', custom: 'مخصص', cannotModify: 'لا يمكن تعديل أدوار النظام'
  },
};

interface Role {
  roleId: number; roleName: string; roleNameAr: string; description?: string;
  isSystemRole: boolean; isActive: boolean;
}

const RolesPage: React.FC = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { mode } = useTheme();
  const t = translations[language];
  const isRTL = language === 'ar';
  const isDark = mode === 'dark';
  const confirm = useConfirm();

  const [roles, setRoles] = useState<Role[]>([]);
  const [filteredRoles, setFilteredRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({ roleName: '', roleNameAr: '', description: '', isActive: true });

  useEffect(() => { loadRoles(); }, []);
  useEffect(() => { applyFilters(); }, [searchTerm, roles]);

  const loadRoles = async () => {
    setLoading(true);
    try { const { data } = await axiosInstance.get('/Roles'); setRoles(data); }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const applyFilters = () => {
    if (!searchTerm.trim()) { setFilteredRoles(roles); return; }
    const term = searchTerm.toLowerCase();
    setFilteredRoles(roles.filter(r => r.roleName.toLowerCase().includes(term) || r.roleNameAr.toLowerCase().includes(term)));
  };

  const handleAdd = () => {
    setSelectedRole(null);
    setFormData({ roleName: '', roleNameAr: '', description: '', isActive: true });
    setError(''); setDialogOpen(true);
  };

  const handleEdit = (role: Role) => {
    if (role.isSystemRole) return;
    setSelectedRole(role);
    setFormData({ roleName: role.roleName, roleNameAr: role.roleNameAr, description: role.description || '', isActive: role.isActive });
    setError(''); setDialogOpen(true);
  };

  const handleDelete = async (id: number, isSystem: boolean) => {
    if (isSystem) return;
    const ok = await confirm({ message: t.confirmDelete, severity: 'error' });
    if (!ok) return;
    try { await axiosInstance.delete(`/Roles/${id}`); loadRoles(); }
    catch (err) { console.error(err); }
  };

  const handleSave = async () => {
    try {
      if (selectedRole) await axiosInstance.put(`/Roles/${selectedRole.roleId}`, { ...formData, roleId: selectedRole.roleId });
      else await axiosInstance.post('/Roles', formData);
      setDialogOpen(false); loadRoles();
    } catch (err: any) { setError(err.response?.data?.message || 'Error'); }
  };

  const stats = {
    total: roles.length,
    system: roles.filter(r => r.isSystemRole).length,
    custom: roles.filter(r => !r.isSystemRole).length,
    active: roles.filter(r => r.isActive).length
  };

  const getRoleIcon = (role: Role) => {
    if (role.roleName === 'Super Admin') return <ShieldIcon />;
    if (role.roleName === 'Admin') return <VerifiedUserIcon />;
    return <RoleIcon />;
  };

  const getRoleGradient = (role: Role) => {
    if (role.roleName === 'Super Admin') return 'linear-gradient(135deg, #ff6b6b 0%, #ee5a5a 100%)';
    if (role.roleName === 'Admin') return 'linear-gradient(135deg, #ffa726 0%, #ff9800 100%)';
    if (role.isSystemRole) return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
    return 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)';
  };

  return (
    <Box sx={{ p: 3 }} dir={isRTL ? 'rtl' : 'ltr'}>
      <Fade in timeout={500}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" fontWeight="bold" sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', mb: 1 }}>{t.title}</Typography>
          <Typography variant="body1" color="text.secondary">{t.subtitle}</Typography>
        </Box>
      </Fade>

      <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
        {[
          { value: stats.total, label: t.totalRoles, icon: <RoleIcon />, gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
          { value: stats.system, label: t.systemRoles, icon: <LockIcon />, gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
          { value: stats.custom, label: t.customRoles, icon: <LockOpenIcon />, gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' },
          { value: stats.active, label: t.activeRoles, icon: <SecurityIcon />, gradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' },
        ].map((stat, index) => (
          <Zoom in timeout={300 + index * 100} key={stat.label}>
            <Card elevation={0} sx={{ flex: '1 1 200px', minWidth: 180, background: stat.gradient, color: 'white', borderRadius: 3, transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 20px 40px rgba(0,0,0,0.2)' } }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="h3" fontWeight="bold" sx={{ textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>{stat.value}</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>{stat.label}</Typography>
                  </Box>
                  <Box sx={{ opacity: 0.3, transform: 'scale(2)' }}>{stat.icon}</Box>
                </Box>
              </CardContent>
            </Card>
          </Zoom>
        ))}
      </Box>

      <Fade in timeout={700}>
        <Paper elevation={0} sx={{ p: 2.5, mb: 3, borderRadius: 3, background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.9)', backdropFilter: 'blur(10px)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(102, 126, 234, 0.2)'}` }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <TextField placeholder={t.search} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} size="small" sx={{ minWidth: 300, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#667eea' }} /></InputAdornment> }} />
            <Stack direction="row" spacing={1.5}>
              <Tooltip title={t.refresh} arrow>
                <IconButton onClick={loadRoles} sx={{ bgcolor: isDark ? 'rgba(102, 126, 234, 0.2)' : 'rgba(102, 126, 234, 0.1)', '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.3)', transform: 'rotate(180deg)' }, transition: 'all 0.3s ease' }}>
                  <RefreshIcon sx={{ color: '#667eea' }} />
                </IconButton>
              </Tooltip>
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd} sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderRadius: 2, px: 3, boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)', '&:hover': { boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)', transform: 'translateY(-2px)' }, transition: 'all 0.3s ease' }}>{t.addRole}</Button>
            </Stack>
          </Box>
        </Paper>
      </Fade>

      <Fade in timeout={900}>
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, overflow: 'hidden', background: isDark ? 'rgba(255,255,255,0.05)' : '#fff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}` }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}><CircularProgress sx={{ color: '#667eea' }} /></Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                  {[t.roleName, t.roleNameAr, t.description, t.isSystem, t.status, t.actions].map(h => (
                    <TableCell key={h} sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.95rem', py: 2 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRoles.length === 0 ? (
                  <TableRow><TableCell colSpan={6} align="center" sx={{ py: 8 }}><RoleIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} /><Typography color="text.secondary" variant="h6">{t.noData}</Typography></TableCell></TableRow>
                ) : filteredRoles.map((role) => (
                  <TableRow key={role.roleId} hover sx={{ '&:hover': { bgcolor: isDark ? 'rgba(102, 126, 234, 0.1)' : 'rgba(102, 126, 234, 0.05)' }, transition: 'background 0.2s ease', opacity: role.isSystemRole ? 0.9 : 1 }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ p: 1, borderRadius: 2, background: getRoleGradient(role), color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{getRoleIcon(role)}</Box>
                        <Typography fontWeight="600" color="primary.main">{role.roleName}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell><Typography fontWeight="500">{role.roleNameAr}</Typography></TableCell>
                    <TableCell><Typography variant="body2" component="div" color="text.secondary">{role.description || '-'}</Typography></TableCell>
                    <TableCell>
                      <Chip icon={role.isSystemRole ? <LockIcon sx={{ fontSize: 16 }} /> : <LockOpenIcon sx={{ fontSize: 16 }} />} label={role.isSystemRole ? t.system : t.custom} size="small"
                        sx={{ background: role.isSystemRole ? 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' : 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: '#fff', fontWeight: 600, '& .MuiChip-icon': { color: '#fff' } }} />
                    </TableCell>
                    <TableCell>
                      <Chip icon={role.isActive ? <SecurityIcon sx={{ fontSize: 16 }} /> : undefined} label={role.isActive ? t.active : t.inactive} size="small"
                        sx={{ background: role.isActive ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' : 'linear-gradient(135deg, #eb3349 0%, #f45c43 100%)', color: '#fff', fontWeight: 600, '& .MuiChip-icon': { color: '#fff' } }} />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title={role.isSystemRole ? t.cannotModify : t.edit} arrow>
                          <span>
                            <IconButton size="small" onClick={() => handleEdit(role)} disabled={role.isSystemRole} sx={{ color: '#667eea', '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.1)', transform: 'scale(1.1)' }, '&.Mui-disabled': { color: 'text.disabled' } }}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={t.managePermissions} arrow>
                          <IconButton size="small" onClick={() => navigate(`/settings/role-permissions/${role.roleId}`)} sx={{ color: '#f093fb', '&:hover': { bgcolor: 'rgba(240, 147, 251, 0.1)', transform: 'scale(1.1)' } }}>
                            <SecurityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={role.isSystemRole ? t.cannotModify : t.delete} arrow>
                          <span>
                            <IconButton size="small" onClick={() => handleDelete(role.roleId, role.isSystemRole)} disabled={role.isSystemRole} sx={{ color: '#f45c43', '&:hover': { bgcolor: 'rgba(244, 92, 67, 0.1)', transform: 'scale(1.1)' }, '&.Mui-disabled': { color: 'text.disabled' } }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      </Fade>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth dir={isRTL ? 'rtl' : 'ltr'} PaperProps={{ sx: { borderRadius: 3, background: isDark ? '#1e1e1e' : '#fff' } }}>
        <DialogTitle sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{selectedRole ? <EditIcon /> : <AddIcon />}{selectedRole ? t.editRole : t.createRole}</Box>
          <IconButton onClick={() => setDialogOpen(false)} sx={{ color: 'white' }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label={t.roleName} fullWidth value={formData.roleName} onChange={e => setFormData({ ...formData, roleName: e.target.value })} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              <TextField label={t.roleNameAr} fullWidth value={formData.roleNameAr} onChange={e => setFormData({ ...formData, roleNameAr: e.target.value })} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </Box>
            <TextField label={t.description} fullWidth multiline rows={3} value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            <FormControlLabel control={<Switch checked={formData.isActive} onChange={e => setFormData({ ...formData, isActive: e.target.checked })} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#38ef7d', '& + .MuiSwitch-track': { backgroundColor: '#38ef7d' } } }} />} label={<Typography fontWeight="500">{t.active}</Typography>} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ borderRadius: 2, px: 3 }}>{t.cancel}</Button>
          <Button variant="contained" onClick={handleSave} startIcon={<SaveIcon />} sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderRadius: 2, px: 3, '&:hover': { boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)' } }}>{t.save}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RolesPage;